import React, { useState } from "react";
import { attendanceViewApi, errorMessage, withToken } from "../adminApi";
import { useAdminToast } from "./Toast";
import { Badge, Button, Modal, MapLocation } from "./ui";
import { Icon } from "./icons";
import DeleteAttendanceAction, { canDeleteAttendance } from "./DeleteAttendance";
import { useAuth } from "../../context/AuthContext";
import { formatDate, formatTime, formatMinutes, attendanceFlagColor, attendanceStatusLabel, attendanceStatusText, ANOMALY_HINT } from "../format";

/**
 * MODAL DETAIL ABSEN — dipakai tampilan list & kalender di halaman Absensi.
 */

/** Label sumber aturan telat (transparansi hitung dinamis). */
const RULE_SOURCE = {
  SCHEDULE: "Jadwal",
  WORK_LOCATION: "Lokasi",
  DEFAULT: "Default",
  OFF_DAY: "Libur",
  WFH: "WFH",
  FLEXIBLE_NO_RULE: "Flexible (tanpa jam acuan)",
};

/** Label klasifikasi asal absensi dari Odoo (kontrak Agu 2026, bag. 4.1). */
export const ATTENDANCE_SOURCE = {
  platform: "Platform",
  adms: "Fingerprint",
  manual: "Input Manual (Odoo)",
};

export function initials(name) {
  return (name || "?").trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase();
}

/** State buka/tutup modal + muat detail lengkap (foto, lokasi) saat record diklik. */
export function useAttendanceDetail() {
  const toast = useAdminToast();
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);

  const open = async (row) => {
    if (!row?.id) return; // baris "Absen" (virtual) tak punya detail
    setSelected(row);
    setDetail(null);
    try {
      setDetail(await attendanceViewApi.detail(row.id));
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };
  const close = () => {
    setSelected(null);
    setDetail(null);
  };
  return { selected, detail, open, close };
}

/** Baris label–nilai (gaya minimalis, tanpa ikon). */
function Field({ label, value }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-2 border-b border-slate-100">
      <span className="text-xs text-slate-500 shrink-0">{label}</span>
      <span className="text-sm text-slate-800 font-medium text-right truncate">{value || "-"}</span>
    </div>
  );
}

/** Judul section kecil. */
function Section({ title, hint, children }) {
  return (
    <section className="mt-6">
      <div className="flex items-baseline justify-between mb-2">
        <h4 className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">{title}</h4>
        {hint && <span className="text-[11px] text-slate-400">{hint}</span>}
      </div>
      {children}
    </section>
  );
}

function Selfie({ label, url }) {
  const [broken, setBroken] = useState(false);
  return (
    <div>
      <div className="text-xs text-slate-500 mb-1.5">{label}</div>
      {url && !broken ? (
        <a href={url} target="_blank" rel="noreferrer">
          <img src={url} alt={label} onError={() => setBroken(true)} className="w-full h-40 object-cover rounded-lg bg-slate-50" />
        </a>
      ) : (
        <div className="w-full h-40 rounded-lg bg-slate-50 flex items-center justify-center text-xs text-slate-400">
          Tidak ada foto
        </div>
      )}
    </div>
  );
}

export default function AttendanceViewModal({ open, row, detail, onClose, onDeleted, showRawLogs = false }) {
  const { user } = useAuth();
  if (!open || !row) return null;
  const d = detail || row;
  const present = !!d.check_in;
  const expected = d.expected_check_in ? `${d.expected_check_in}${d.expected_check_out ? `–${d.expected_check_out}` : ""}` : null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Detail Absen"
      subtitle={formatDate(d.date)}
      wide
      footer={
        canDeleteAttendance(user) && d.id ? (
          <>
            <div className="mr-auto"><DeleteAttendanceAction record={d} onDeleted={onDeleted} /></div>
            <Button variant="secondary" onClick={onClose}>Tutup</Button>
          </>
        ) : undefined
      }
    >
      {ANOMALY_HINT[d.status] && (
        <div className="mb-5 rounded-lg bg-amber-50 px-3.5 py-2.5 text-sm text-amber-800 flex items-start gap-2">
          <Icon.Alert size={18} className="mt-0.5 shrink-0" />
          <span>
            <b>{attendanceStatusText(d.status)}</b> — {ANOMALY_HINT[d.status]}
          </span>
        </div>
      )}

      {/* Identitas */}
      <div className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center text-sm font-semibold shrink-0">
          {initials(d.employee_name || d.username)}
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-slate-900 truncate">{d.employee_name || d.username}</div>
          <div className="text-xs text-slate-500 truncate">
            {d.nik || d.username}
            {d.job_title ? ` · ${d.job_title}` : ""}
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <Badge color={attendanceFlagColor(d.status)}>{attendanceStatusLabel(d.status)}</Badge>
          {!present && <Badge color="red">Tidak Hadir</Badge>}
        </div>
      </div>

      {/* Ringkasan jam */}
      <div className="mt-5 grid grid-cols-3 divide-x divide-slate-100 rounded-xl border border-slate-200">
        {[
          ["Check-in", formatTime(d.check_in)],
          ["Check-out", formatTime(d.check_out)],
          ["Total Kerja", formatMinutes(d.worked_minutes)],
        ].map(([label, value]) => (
          <div key={label} className="px-4 py-3">
            <div className="text-xs text-slate-500">{label}</div>
            <div className="mt-0.5 text-lg font-semibold text-slate-900 tabular-nums">{value || "-"}</div>
          </div>
        ))}
      </div>

      {/* Detail */}
      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-8">
        <Field label="Departemen" value={d.department} />
        <Field label="Mode" value={d.work_mode} />
        {d.attendance_source && <Field label="Sumber Absen" value={ATTENDANCE_SOURCE[d.attendance_source] || d.attendance_source} />}
        {expected && <Field label="Jam Ekspektasi" value={`${expected}${RULE_SOURCE[d.rule_source] ? ` · ${RULE_SOURCE[d.rule_source]}` : ""}`} />}
        {d.late_minutes > 0 && <Field label="Telat" value={formatMinutes(d.late_minutes)} />}
      </div>

      {d.flags?.needs_hr_review && (
        <div className="mt-4 flex items-center gap-2 flex-wrap">
          <Badge color={d.flags.review_status === "resolved" ? "green" : "red"}>
            {d.flags.review_status === "resolved" ? "Review HR Selesai" : "Perlu Review HR"}
          </Badge>
          {d.flags.hr_overridden && <Badge color="blue">Sudah dikoreksi HR di Odoo</Badge>}
        </div>
      )}

      {(d.note || d.handling_note) && (
        <div className="mt-4 space-y-1 text-sm text-slate-600">
          {d.note && <p><span className="text-slate-400">Catatan: </span>{d.note}</p>}
          {d.handling_note && <p><span className="text-slate-400">Catatan Odoo: </span>{d.handling_note}</p>}
        </div>
      )}

      <Section title="Foto">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Selfie label="Check-in" url={withToken(d.checkin_photo_url)} />
          <Selfie label="Check-out" url={withToken(d.checkout_photo_url)} />
        </div>
      </Section>

      <Section title="Lokasi" hint="Klik peta untuk buka Google Maps">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <MapLocation label="Check-in" lat={d.checkin_lat} lng={d.checkin_lng} address={d.checkin_address} />
          <MapLocation label="Check-out" lat={d.checkout_lat} lng={d.checkout_lng} address={d.checkout_address} />
        </div>
      </Section>

      {showRawLogs && (
        <>
          <h4 className="mt-6 mb-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400">Log Mentah (audit)</h4>
          <div className="border border-slate-200 rounded-xl overflow-hidden">
            {!detail ? (
              <div className="p-3 text-sm text-slate-400">Memuat log…</div>
            ) : (detail.raw_logs || []).length === 0 ? (
              <div className="p-3 text-sm text-slate-400">Tidak ada log mentah.</div>
            ) : (
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-slate-500 border-b border-slate-200 bg-slate-50">
                    <th className="px-3 py-2">Tipe</th>
                    <th className="px-3 py-2">Waktu</th>
                    <th className="px-3 py-2">Mode</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.raw_logs.map((log) => (
                    <tr key={log.id} className="border-b border-slate-100 last:border-0">
                      <td className="px-3 py-2">{log.type}</td>
                      <td className="px-3 py-2">{formatTime(log.event_time)}</td>
                      <td className="px-3 py-2">{log.work_mode || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </Modal>
  );
}
